import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { localToday } from '../../utils/dateUtils';
import './saude.css';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/header';
import Titulo from '../../components/Titulo/titulo';
import SearchInternal from '../../components/SearchInternal/SearchInternal';
import PageHeader from '../../components/PageHeader';
import { useInternals } from '../../context/internalContext';
import { useAuxiliaries } from '../../context/auxiliariesContext';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useUsers } from '../../context/usersContext';
import useSubmitLock from '../../utils/useSubmitLock';
import BinaryFileLink from '../../components/BinaryFileLink';
import { BINARY_RESOURCES, hasBinaryField } from '../../utils/binaryFile';

const TABS = ['Triagem', 'Exames', 'Vigilância', 'Tratamento', 'Encaminhamentos', 'Encerramento'];


const todayISO = () => localToday();

const formatDateBr = (value) => {
  if (!value) return '-';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('pt-BR');
};

const normalizeNonNegativeInteger = (value) => String(value || '').replace(/[^\d]/g, '');

const normalizeNonNegativeDecimal = (value) => {
  const sanitized = String(value || '').replace(',', '.').replace(/[^0-9.]/g, '');
  const [integerPart, ...decimalParts] = sanitized.split('.');
  if (!decimalParts.length) return integerPart;
  return `${integerPart}.${decimalParts.join('')}`;
};

const normalizeCatalogs = (raw) => {
  const pick = (...keys) => {
    for (const k of keys) {
      if (Array.isArray(raw?.[k]) && raw[k].length > 0) return raw[k];
    }
    return [];
  };
  return {
    examTypes: pick('exam_types', 'exam_request_types'),
    referenceServices: pick('reference_services'),
    communicationMethods: pick('communication_methods'),
    medicationSupplyStatuses: pick('medication_supply_statuses'),
    therapeuticSchemes: pick('therapeutic_schemes'),
    referralNeeds: pick('referral_needs'),
    closureTypes: pick('closure_types'),
    clinicalForms: pick('clinical_forms'),
    pddResults: pick('pdd_results'),
    hivStatuses: pick('hiv_statuses'),
    tdoStatuses: pick('tdo_statuses'),
    drugs: pick('drugs'),
  };
};

const emptyExamEntry = () => ({
  checked: false,
  solicitadoPara: '',
  dataSolicitacao: todayISO(),
  resultadoPositivo: null,
  file: null,
});

const emptyReferralEntry = () => ({
  checked: false,
  solicitadoPara: '',
  dataSolicitacao: todayISO(),
  file: null,
});

const Tuberculose = () => {
  const location = useLocation();
  const { internalId } = location.state || {};
  const {
    listInternals,
    listTuberculosis,
    createTuberculosis,
    updateTuberculosis,
    listTuberculosisQuestions,
    listTuberculosisCatalogs,
  } = useInternals();
  const { listRemedies } = useAuxiliaries();
  const { userInfo, buildUnitReadParams } = useUsers();
  const { isSubmitting, runWithSubmitLock } = useSubmitLock();

  const [internalDetails, setInternalDetails] = useState(null);
  const [tuberculosisId, setTuberculosisId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [maxUnlockedTab, setMaxUnlockedTab] = useState(0);

  const [questions, setQuestions] = useState([]);
  const [catalogs, setCatalogs] = useState({
    examTypes: [],
    referenceServices: [],
    communicationMethods: [],
    medicationSupplyStatuses: [],
    therapeuticSchemes: [],
    referralNeeds: [],
    closureTypes: [],
    clinicalForms: [],
    pddResults: [],
    hivStatuses: [],
    tdoStatuses: [],
    drugs: [],
  });
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [savedTabs, setSavedTabs] = useState(new Set());
  const cleanSnapshotsRef = React.useRef({});

  // triagem: { [questionId]: true | false | null }
  const [triagem, setTriagem] = useState({});
  // examState: { [examTypeId]: { checked, solicitadoPara, dataSolicitacao, resultadoPositivo, file } }
  const [examState, setExamState] = useState({});
  // triageReferralState: { [referenceServiceId]: { checked, solicitadoPara, dataSolicitacao, file } }
  const [triageReferralState, setTriageReferralState] = useState({});
  const [examArticulacao, setExamArticulacao] = useState('');
  const [sinanFile, setSinanFile] = useState(null);
  // ids dos sub-registros que já têm arquivo (o arquivo sai pela rota binary de cada um)
  const [existingFiles, setExistingFiles] = useState({
    sinanFileId: null,
    examFileIds: {},
    referralFileIds: {},
    closureFileId: null,
  });

  const [vigilancia, setVigilancia] = useState({
    dataComunicacao: todayISO(),
    meioComunicacao: '',
    statusMedicamentos: '',
  });

  const [evolutionForm, setEvolutionForm] = useState({
    data: '', peso: '', esquema: '', tdo: '', intercorrencias: '', observacoes: '',
  });
  const [evolutions, setEvolutions] = useState([]);

  const [forwardingForm, setForwardingForm] = useState({ data: '', servico: '', necessidade: '' });
  const [forwardings, setForwardings] = useState([]);

  const [encerramento, setEncerramento] = useState({ tipo: '', data: '', file: null, observacoes: '' });

  const [transferencia, setTransferencia] = useState({
    sinanNumero: '',
    dataNotificacao: '',
    formaClinica: '',
    extrapulmonaryLocation: '',
    ppd: '',
    drogas: [],
    otherDrugsDescription: '',
    pplDias: '',
    tdoRealizado: null,
    hivStatus: '',
    otherExamsDescription: '',
    inicioTratamento: '',
  });
  const [baciloscopiaSamples, setBaciloscopiaSamples] = useState([]);

  // ─── derived ──────────────────────────────────────────────────────────────

  const allTriagemAnswered = useMemo(
    () => questions.length > 0 && questions.every((q) => triagem[q.id] === true || triagem[q.id] === false),
    [triagem, questions],
  );

  const hasSuspectedCase = useMemo(
    () => Object.values(triagem).some((v) => v === true),
    [triagem],
  );

  const selectedExams = useMemo(
    () => Object.entries(examState).filter(([, e]) => e.checked),
    [examState],
  );

  const isTransferenciaType = useMemo(() => {
    const tipo = catalogs.closureTypes.find((t) => t.id === encerramento.tipo);
    return !!(tipo?.code === 'transferencia' || tipo?.name?.toLowerCase().includes('transfer'));
  }, [encerramento.tipo, catalogs.closureTypes]);

const otherDrugItem = useMemo(
    () => catalogs.drugs.find((d) => d.code === 'other' || d.name?.toLowerCase().includes('outr')),
    [catalogs.drugs],
  );

  const isOtherDrugSelected = useMemo(
    () => !!(otherDrugItem && transferencia.drogas.includes(otherDrugItem.id)),
    [otherDrugItem, transferencia.drogas],
  );

  const dirtyTabs = useMemo(() => {
    const dirty = new Set();
    const snaps = cleanSnapshotsRef.current;
    if (snaps[0] !== undefined && JSON.stringify(triagem) !== snaps[0]) dirty.add(0);
    if (snaps[1] !== undefined) {
      try {
        const { examArticulacao: snapArt, examOverrides = {}, referralOverrides = {} } = JSON.parse(snaps[1]);
        const curExam = {};
        Object.entries(examState).forEach(([id, e]) => {
          if (e.checked) curExam[id] = { solicitadoPara: e.solicitadoPara, dataSolicitacao: e.dataSolicitacao, resultadoPositivo: e.resultadoPositivo };
        });
        const curRef = {};
        Object.entries(triageReferralState).forEach(([id, r]) => {
          if (r.checked) curRef[id] = { solicitadoPara: r.solicitadoPara, dataSolicitacao: r.dataSolicitacao };
        });
        const sort = (o) => JSON.stringify(Object.keys(o).sort().reduce((a, k) => { a[k] = o[k]; return a; }, {}));
        if (!!sinanFile || examArticulacao !== snapArt || sort(curExam) !== sort(examOverrides) || sort(curRef) !== sort(referralOverrides)) dirty.add(1);
      } catch { dirty.add(1); }
    }
    if (snaps[2] !== undefined && JSON.stringify(vigilancia) !== snaps[2]) dirty.add(2);
    if (snaps[3] !== undefined && JSON.stringify(evolutions) !== snaps[3]) dirty.add(3);
    if (snaps[4] !== undefined && JSON.stringify(forwardings) !== snaps[4]) dirty.add(4);
    if (snaps[5] !== undefined) {
      const encSnap = { tipo: encerramento.tipo, data: encerramento.data, observacoes: encerramento.observacoes };
      if (!!encerramento.file || JSON.stringify({ encerramento: encSnap, transferencia, baciloscopiaSamples }) !== snaps[5]) dirty.add(5);
    }
    return dirty;
  }, [triagem, examState, triageReferralState, examArticulacao, sinanFile, vigilancia, evolutions, forwardings, encerramento, transferencia, baciloscopiaSamples]);

  // ─── effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      setLoadingCatalogs(true);
      try {
        const [catResp, questResp, drugsResp] = await Promise.all([
          listTuberculosisCatalogs(),
          listTuberculosisQuestions(),
          listRemedies({ indication_name: 'tuberculose' }),
        ]);
        const normalized = normalizeCatalogs(catResp);
        const drugs = drugsResp?.results || drugsResp || [];
        setCatalogs({ ...normalized, drugs });
        const allQ = questResp?.results || questResp || [];
        setQuestions(
          allQ.filter((q) => {
            const sub = String(q?.section_detail?.subname || '').toLowerCase();
            return sub.includes('tuberculose') || sub.includes('tuberculosis');
          }),
        );
      } catch (err) {
        console.error('Erro ao carregar catálogos/questões:', err);
      } finally {
        setLoadingCatalogs(false);
      }
    };
    load();
  }, [listTuberculosisCatalogs, listTuberculosisQuestions, listRemedies]);

  useEffect(() => {
    if (!catalogs.examTypes.length) return;
    setExamState((prev) => {
      const next = {};
      catalogs.examTypes.forEach((t) => {
        next[t.id] = prev[t.id] || emptyExamEntry();
      });
      return next;
    });
  }, [catalogs.examTypes]);

  useEffect(() => {
    if (!catalogs.referenceServices.length) return;
    setTriageReferralState((prev) => {
      const next = {};
      catalogs.referenceServices.forEach((s) => {
        next[s.id] = prev[s.id] || emptyReferralEntry();
      });
      return next;
    });
  }, [catalogs.referenceServices]);

  useEffect(() => {
    if (!internalId) return;
    (async () => {
      try {
        const response = await listInternals(buildUnitReadParams({ id: internalId }));
        const internal = response?.results?.[0];
        if (internal) setInternalDetails(internal);
      } catch (err) {
        console.error('Erro ao carregar interno:', err);
      }
    })();
  }, [internalId, listInternals, buildUnitReadParams]);

  useEffect(() => {
    if (!internalDetails?.id) return;
    setSavedTabs(new Set());
    cleanSnapshotsRef.current = {};
    setExistingFiles({ sinanFileId: null, examFileIds: {}, referralFileIds: {}, closureFileId: null });
    (async () => {
      try {
        const resp = await listTuberculosis({ internal: internalDetails.id });
        const latest = resp?.results?.[0];
        if (!latest?.id) return;
        setTuberculosisId(latest.id);

        const newSavedTabs = new Set();
        const newSnapshots = {};

        // Tab 0 — Triagem
        const nextTriagem = {};
        (latest.answers_detail || []).forEach((a) => {
          const qId = a.question ?? a.question_id ?? a.id;
          const text = String(a.response || a.response_text || '').toLowerCase();
          nextTriagem[qId] = text === 'sim' || text === 'true';
        });
        setTriagem(nextTriagem);
        if (Object.keys(nextTriagem).length > 0) {
          newSnapshots[0] = JSON.stringify(nextTriagem);
          newSavedTabs.add(0);
        }

        // Tab 1 — Exames
        const nextArticulacao = latest.exam_articulation?.articulation_exam_details || '';
        setExamArticulacao(nextArticulacao);
        const newExamFileIds = {};
        (latest.exam_requests || []).forEach((r) => { if (hasBinaryField(r, 'file')) newExamFileIds[r.exam_or_test_type] = r.id; });
        const newReferralFileIds = {};
        (latest.triage_referrals || []).forEach((r) => { if (hasBinaryField(r, 'file')) newReferralFileIds[r.reference_service] = r.id; });
        const newSinanFileId = hasBinaryField(latest.exam_articulation, 'sinan_file') ? latest.exam_articulation.id : null;
        if (latest.exam_articulation || latest.exam_requests?.length > 0 || latest.triage_referrals?.length > 0) {
          const examOverrides = {};
          (latest.exam_requests || []).forEach((r) => {
            examOverrides[r.exam_or_test_type] = {
              checked: true,
              solicitadoPara: r.requested_to || '',
              dataSolicitacao: r.request_date || todayISO(),
              resultadoPositivo: r.result_positive,
              file: null,
            };
          });
          if (Object.keys(examOverrides).length) {
            setExamState((prev) => ({
              ...prev,
              ...Object.fromEntries(Object.entries(examOverrides).map(([id, val]) => [Number(id), val])),
            }));
          }
          const referralOverrides = {};
          (latest.triage_referrals || []).forEach((r) => {
            referralOverrides[r.reference_service] = {
              checked: true,
              solicitadoPara: r.requested_to || '',
              dataSolicitacao: r.request_date || todayISO(),
              file: null,
            };
          });
          if (Object.keys(referralOverrides).length) {
            setTriageReferralState((prev) => ({
              ...prev,
              ...Object.fromEntries(Object.entries(referralOverrides).map(([id, val]) => [Number(id), val])),
            }));
          }
          newSnapshots[1] = JSON.stringify({
            examArticulacao: nextArticulacao,
            examOverrides: Object.fromEntries(Object.entries(examOverrides).map(([id, v]) => [id, { solicitadoPara: v.solicitadoPara, dataSolicitacao: v.dataSolicitacao, resultadoPositivo: v.resultadoPositivo }])),
            referralOverrides: Object.fromEntries(Object.entries(referralOverrides).map(([id, v]) => [id, { solicitadoPara: v.solicitadoPara, dataSolicitacao: v.dataSolicitacao }])),
          });
          newSavedTabs.add(1);
        }

        // Tab 2 — Vigilância
        if (latest.sms_surveillance) {
          const nextVigilancia = {
            dataComunicacao: latest.sms_surveillance.communication_date || todayISO(),
            meioComunicacao: latest.sms_surveillance.communication_method || '',
            statusMedicamentos: latest.sms_surveillance.medication_supply_status || '',
          };
          setVigilancia(nextVigilancia);
          newSnapshots[2] = JSON.stringify(nextVigilancia);
          newSavedTabs.add(2);
        }

        // Tab 3 — Tratamento
        if (latest.treatment_follow_ups?.length) {
          const nextEvolutions = latest.treatment_follow_ups.map((e) => ({
            data: e.date,
            peso: e.weight_kg || '',
            esquema: e.therapeutic_scheme || '',
            tdo: e.tdo_status || '',
            intercorrencias: e.intercurrences || '',
            observacoes: e.general_observation || '',
          }));
          setEvolutions(nextEvolutions);
          newSnapshots[3] = JSON.stringify(nextEvolutions);
          newSavedTabs.add(3);
        }

        // Tab 4 — Encaminhamentos
        if (latest.complex_referrals?.length) {
          const nextForwardings = latest.complex_referrals.map((f) => ({
            data: f.referral_date,
            servico: f.reference_service,
            necessidade: f.referral_need,
          }));
          setForwardings(nextForwardings);
          newSnapshots[4] = JSON.stringify(nextForwardings);
          newSavedTabs.add(4);
        }

        // Tab 5 — Encerramento
        const newClosureFileId = hasBinaryField(latest.closure_record, 'closure_file') ? latest.closure_record.id : null;
        if (latest.closure_record) {
          const nextEncerramento = {
            tipo: latest.closure_record.closure_type || '',
            data: latest.closure_record.closure_date || '',
            file: null,
            observacoes: latest.closure_record.closure_observation || '',
          };
          setEncerramento(nextEncerramento);

          let nextTransferencia = null;
          let nextBaciloscopiaSamples = [];

          if (latest.transfer_bulletin) {
            const tb = latest.transfer_bulletin;
            nextBaciloscopiaSamples = (tb.sputum_smear_samples || []).map((s) => ({
              id: s.id || Date.now() + Math.random(),
              resultado: s.result_positive,
            }));
            setBaciloscopiaSamples(nextBaciloscopiaSamples);
            nextTransferencia = {
              sinanNumero: tb.sinan_number || '',
              dataNotificacao: tb.notification_date || '',
              formaClinica: tb.clinical_form || '',
              extrapulmonaryLocation: tb.extrapulmonary_location || '',
              ppd: tb.pdd_result || '',
              drogas: (tb.used_drug_items || []).map((d) => d.drug),
              otherDrugsDescription: tb.other_drugs_description || '',
              pplDias: tb.ppl_medication_days != null ? String(tb.ppl_medication_days) : '',
              tdoRealizado: tb.tdo_performed ?? null,
              hivStatus: tb.hiv_status || '',
              otherExamsDescription: tb.other_exams_description || '',
              inicioTratamento: tb.treatment_start_date || '',
            };
            setTransferencia(nextTransferencia);
          }

          const encSnap = { tipo: nextEncerramento.tipo, data: nextEncerramento.data, observacoes: nextEncerramento.observacoes };
          newSnapshots[5] = JSON.stringify({ encerramento: encSnap, transferencia: nextTransferencia, baciloscopiaSamples: nextBaciloscopiaSamples });
          newSavedTabs.add(5);
        }

        cleanSnapshotsRef.current = newSnapshots;
        setSavedTabs(newSavedTabs);
        setExistingFiles({
          sinanFileId: newSinanFileId,
          examFileIds: newExamFileIds,
          referralFileIds: newReferralFileIds,
          closureFileId: newClosureFileId,
        });

        const loadedHasSuspectedCase = Object.values(nextTriagem).some((v) => v === true);
        const maxTab = newSavedTabs.size > 0 && loadedHasSuspectedCase
          ? Math.min(Math.max(...newSavedTabs) + 1, TABS.length - 1)
          : 0;
        setMaxUnlockedTab(maxTab);

      } catch (err) {
        console.error('Erro ao carregar registro de tuberculose:', err);
      }
    })();
  }, [internalDetails?.id, listTuberculosis]);


  // POST inicial — só aba 0
  const buildCreateFormData = useCallback(() => {
    const fd = new FormData();
    fd.append('internal', internalDetails?.id);
    if (userInfo?.id) fd.append('health_professional', userInfo.id);
    const answersArray = questions
      .filter((q) => triagem[q.id] === true || triagem[q.id] === false)
      .map((q) => ({ question_id: q.id, response_text: triagem[q.id] ? 'Sim' : 'Não' }));
    if (answersArray.length) fd.append('answers', JSON.stringify(answersArray));
    return fd;
  }, [internalDetails, userInfo, questions, triagem]);

  // PATCH — sempre inclui dados de todas as abas salvas + aba atual
  // Evita que o backend interprete campos ausentes como "limpar"
  const buildPatchFormData = useCallback((currentTab) => {
    const fd = new FormData();

    if (currentTab === 0) {
      const answersArray = questions
        .filter((q) => triagem[q.id] === true || triagem[q.id] === false)
        .map((q) => ({ question_id: q.id, response_text: triagem[q.id] ? 'Sim' : 'Não' }));
      if (answersArray.length) fd.append('answers', JSON.stringify(answersArray));
    }

    if (currentTab === 1) {
      fd.append('exam_articulation', JSON.stringify({ articulation_exam_details: examArticulacao || null }));
      if (sinanFile) fd.append('exam_articulation.sinan_file', sinanFile);
      const examRequests = Object.entries(examState)
        .filter(([, e]) => e.checked)
        .map(([id, e]) => ({
          exam_or_test_type: Number(id),
          requested_to: e.solicitadoPara || '',
          request_date: e.dataSolicitacao,
          result_positive: e.resultadoPositivo,
        }));
      if (examRequests.length) {
        fd.append('exam_requests', JSON.stringify(examRequests));
        examRequests.forEach((req, idx) => {
          const entry = examState[req.exam_or_test_type];
          if (entry?.file) fd.append(`exam_requests[${idx}][file]`, entry.file);
        });
      }
      const triageReferrals = Object.entries(triageReferralState)
        .filter(([, r]) => r.checked)
        .map(([id, r]) => ({
          reference_service: Number(id),
          requested_to: r.solicitadoPara || '',
          request_date: r.dataSolicitacao,
        }));
      if (triageReferrals.length) {
        fd.append('triage_referrals', JSON.stringify(triageReferrals));
        triageReferrals.forEach((ref, idx) => {
          const entry = triageReferralState[ref.reference_service];
          if (entry?.file) fd.append(`triage_referrals[${idx}][file]`, entry.file);
        });
      }
    }

    if (currentTab === 2) {
      fd.append('sms_surveillance', JSON.stringify({
        communication_date: vigilancia.dataComunicacao || null,
        communication_method: vigilancia.meioComunicacao || null,
        medication_supply_status: vigilancia.statusMedicamentos || null,
      }));
    }

    if (currentTab === 3) {
      fd.append('treatment_follow_ups', JSON.stringify(
        evolutions.map((e) => {
          const item = { date: e.data, tdo_status: e.tdo };
          if (e.esquema) item.therapeutic_scheme = e.esquema;
          if (e.peso) item.weight_kg = e.peso;
          if (e.intercorrencias) item.intercurrences = e.intercorrencias;
          if (e.observacoes) item.general_observation = e.observacoes;
          return item;
        }),
      ));
    }

    if (currentTab === 4) {
      fd.append('complex_referrals', JSON.stringify(
        forwardings.map((f) => ({
          referral_date: f.data,
          reference_service: f.servico,
          referral_need: f.necessidade,
        })),
      ));
    }

    if (currentTab === 5) {
      fd.append('closure_record', JSON.stringify({
        closure_type: encerramento.tipo,
        closure_date: encerramento.data || null,
        closure_observation: encerramento.observacoes || null,
      }));
      if (encerramento.file) fd.append('closure_record.closure_file', encerramento.file);
      const closureTypeObj = catalogs.closureTypes.find((t) => t.id === encerramento.tipo);
      const isTransfer = closureTypeObj?.code === 'transferencia'
        || closureTypeObj?.name?.toLowerCase().includes('transfer');
      if (isTransfer) {
        const bulletinPayload = {
          sinan_number: transferencia.sinanNumero || null,
          notification_date: transferencia.dataNotificacao || null,
          clinical_form: transferencia.formaClinica || null,
          extrapulmonary_location: transferencia.extrapulmonaryLocation || null,
          pdd_result: transferencia.ppd || null,
          used_drugs: transferencia.drogas,
          other_drugs_description: transferencia.otherDrugsDescription || null,
          ppl_medication_days: transferencia.pplDias ? Number(transferencia.pplDias) : null,
          tdo_performed: transferencia.tdoRealizado,
          hiv_status: transferencia.hivStatus || null,
          other_exams_description: transferencia.otherExamsDescription || null,
          treatment_start_date: transferencia.inicioTratamento || null,
        };
        bulletinPayload.sputum_smear_samples = baciloscopiaSamples.map((s, i) => ({
          sample_number: i + 1,
          result_positive: s.resultado,
        }));
        fd.append('transfer_bulletin', JSON.stringify(bulletinPayload));
      }
    }

    return fd;
  }, [
    questions, triagem, examState, triageReferralState, examArticulacao, sinanFile,
    vigilancia, evolutions, forwardings, encerramento, transferencia, baciloscopiaSamples,
    catalogs.closureTypes,
  ]);

  // ─── handlers ─────────────────────────────────────────────────────────────

  const handleInternalSelect = async (internal) => {
    setInternalDetails(internal);
    try {
      if (!internal?.id) return;
      const response = await listInternals(buildUnitReadParams({ id: internal.id }));
      const selected = response?.results?.[0];
      if (selected) setInternalDetails(selected);
    } catch (err) {
      console.error('Erro ao carregar interno:', err);
    }
  };

  const unlockNextTab = () => {
    setMaxUnlockedTab((prev) => {
      const next = Math.min(prev + 1, TABS.length - 1);
      setActiveTab(next);
      return next;
    });
  };

  const goToTab = (tabIndex) => {
    if (tabIndex <= maxUnlockedTab) setActiveTab(tabIndex);
  };

  const validateAndContinue = async () => {
    if (!internalDetails?.id) {
      toast.error('Selecione um interno para iniciar o fluxo.');
      return;
    }
    if (activeTab < TABS.length - 1 && savedTabs.has(activeTab) && !dirtyTabs.has(activeTab)) {
      setActiveTab(activeTab + 1);
      return;
    }
    if (activeTab === 0) {
      if (!allTriagemAnswered) { toast.error('Responda todas as perguntas da triagem.'); return; }
    }
    if (activeTab === 1) {
      if (!selectedExams.length) { toast.error('Selecione ao menos um exame.'); return; }
      if (!sinanFile) { toast.error('Anexe a ficha SINAN para continuar.'); return; }
    }
    if (activeTab === 2) {
      if (!vigilancia.dataComunicacao || !vigilancia.meioComunicacao || !vigilancia.statusMedicamentos) {
        toast.error('Preencha os dados de articulação com a vigilância.'); return;
      }
    }
    if (activeTab === 3) {
      if (!evolutions.length) { toast.error('Adicione ao menos uma evolução para continuar.'); return; }
    }
    if (activeTab === 4) {
      if (!forwardings.length) { toast.error('Registre ao menos um encaminhamento para continuar.'); return; }
    }
    if (activeTab === 5) {
      if (!encerramento.tipo || !encerramento.data) { toast.error('Preencha tipo e data de encerramento.'); return; }
      if (!encerramento.file) { toast.error('Anexe o boletim de alta/transferência.'); return; }
      if (isTransferenciaType && (!transferencia.dataNotificacao || !transferencia.formaClinica || transferencia.tdoRealizado === null)) {
        toast.error('Preencha os dados mínimos do boletim de transferência.'); return;
      }
    }

    await runWithSubmitLock(async () => {
      let result;
      if (activeTab === 0 && !tuberculosisId) {
        result = await createTuberculosis(buildCreateFormData(), true);
        if (result?.id) setTuberculosisId(result.id);
      } else {
        result = await updateTuberculosis(tuberculosisId, buildPatchFormData(activeTab), true);
      }
      if (!result) return;


      if (activeTab === TABS.length - 1) {
        toast.success('Prontuário de tuberculose finalizado com sucesso!');
      } else if (activeTab === 0 && !hasSuspectedCase) {
        toast.info('Triagem salva. Nenhum caso suspeito — etapas seguintes bloqueadas.');
        setMaxUnlockedTab(0);
      } else {
        unlockNextTab();
      }
    });
  };

  const handleExamToggle = (examId) => {
    setExamState((prev) => ({ ...prev, [examId]: { ...prev[examId], checked: !prev[examId].checked } }));
  };

  const handleExamField = (examId, field, value) => {
    setExamState((prev) => ({ ...prev, [examId]: { ...prev[examId], [field]: value } }));
  };

  const handleReferralToggle = (serviceId) => {
    setTriageReferralState((prev) => ({
      ...prev,
      [serviceId]: { ...prev[serviceId], checked: !prev[serviceId].checked },
    }));
  };

  const handleReferralField = (serviceId, field, value) => {
    setTriageReferralState((prev) => ({ ...prev, [serviceId]: { ...prev[serviceId], [field]: value } }));
  };

  const addEvolution = () => {
    if (!evolutionForm.data || !evolutionForm.tdo) {
      toast.error('Data e Adesão/TDO são obrigatórios para adicionar evolução.'); return;
    }
    if (catalogs.therapeuticSchemes.length && !evolutionForm.esquema) {
      toast.error('Selecione o esquema terapêutico para adicionar evolução.'); return;
    }
    setEvolutions((prev) => ([...prev, { ...evolutionForm }]));
    setEvolutionForm({ data: '', peso: '', esquema: '', tdo: '', intercorrencias: '', observacoes: '' });
  };

  const addForwarding = () => {
    if (!forwardingForm.data || !forwardingForm.servico || !forwardingForm.necessidade) {
      toast.error('Preencha todos os campos do encaminhamento.'); return;
    }
    setForwardings((prev) => ([...prev, { ...forwardingForm }]));
    setForwardingForm({ data: '', servico: '', necessidade: '' });
  };

  const toggleDroga = (drugId) => {
    setTransferencia((prev) => ({
      ...prev,
      drogas: prev.drogas.includes(drugId)
        ? prev.drogas.filter((d) => d !== drugId)
        : [...prev.drogas, drugId],
    }));
  };

  const addBaciloscopiaSample = () => {
    setBaciloscopiaSamples((prev) => ([...prev, { id: Date.now(), resultado: null }]));
  };

  const removeBaciloscopiaSample = (sampleId) => {
    setBaciloscopiaSamples((prev) => (prev.length === 1 ? prev : prev.filter((s) => s.id !== sampleId)));
  };

  const setBaciloscopiaResult = (sampleId, resultado) => {
    setBaciloscopiaSamples((prev) => prev.map((s) => (s.id === sampleId ? { ...s, resultado } : s)));
  };

  // ─── renders ──────────────────────────────────────────────────────────────

  const renderTriagemTab = () => (
    <div className="tb-step-card">
      <h4>1. TRIAGEM DE SINTOMAS RESPIRATÓRIOS</h4>
      {loadingCatalogs && <p className="health-note">Carregando perguntas...</p>}
      <div className="tb-triage-list">
        {questions.map((q) => (
          <div key={q.id} className="tb-triage-item">
            <p>{q.description}</p>
            <div className="tb-yes-no">
              <button
                type="button"
                className={triagem[q.id] === true ? 'active' : ''}
                onClick={() => setTriagem((prev) => ({ ...prev, [q.id]: true }))}
              >SIM</button>
              <button
                type="button"
                className={triagem[q.id] === false ? 'active' : ''}
                onClick={() => setTriagem((prev) => ({ ...prev, [q.id]: false }))}
              >NAO</button>
            </div>
          </div>
        ))}
      </div>
      {hasSuspectedCase && (
        <div className="tb-alert-danger">
          <strong>CASO SUSPEITO DE TB</strong>
          <span>Paciente sintomático. Proceder com investigação laboratorial imediata.</span>
        </div>
      )}
      {allTriagemAnswered && !hasSuspectedCase && (
        <div className="tb-alert-info">
          <strong>NENHUM CASO SUSPEITO</strong>
          <span>Todas as respostas negativas. O protocolo completo de tuberculose não se aplica.</span>
        </div>
      )}
    </div>
  );

  const renderExamesTab = () => (
    <div className="tb-step-card">
      <h4>2. SOLICITACAO E CONTROLE DE EXAMES</h4>
      <div className="tb-exams-grid">
        {catalogs.examTypes.map((t) => {
          const current = examState[t.id] || emptyExamEntry();
          return (
            <div key={t.id} className="tb-exam-card">
              <label className="tb-checkbox-line">
                <input type="checkbox" checked={!!current.checked} onChange={() => handleExamToggle(t.id)} />
                {t.name}
              </label>
              {current.checked && (
                <div className="tb-exam-fields">
                  <input
                    type="text"
                    placeholder="A quem foi solicitado"
                    value={current.solicitadoPara}
                    onChange={(e) => handleExamField(t.id, 'solicitadoPara', e.target.value)}
                  />
                  <input
                    type="date"
                    value={current.dataSolicitacao}
                    onChange={(e) => handleExamField(t.id, 'dataSolicitacao', e.target.value)}
                  />
                  <div className="tb-result-toggle">
                    <button
                      type="button"
                      className={current.resultadoPositivo === true ? 'active positive' : ''}
                      onClick={() => handleExamField(t.id, 'resultadoPositivo', true)}
                    >POSITIVO</button>
                    <button
                      type="button"
                      className={current.resultadoPositivo === false ? 'active' : ''}
                      onClick={() => handleExamField(t.id, 'resultadoPositivo', false)}
                    >NEGATIVO</button>
                  </div>
                  {existingFiles.examFileIds[t.id] && !current.file && (
                    <BinaryFileLink resource={BINARY_RESOURCES.tuberculosisExamRequest} id={existingFiles.examFileIds[t.id]} field="file" className="tb-file-link">Ver documento</BinaryFileLink>
                  )}
                  <input
                    type="file"
                    accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleExamField(t.id, 'file', e.target.files?.[0] || null)}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {catalogs.referenceServices.length > 0 && (
        <>
          <h5 style={{ marginTop: 16 }}>ENCAMINHAMENTOS PARA SERVIÇOS DE REFERÊNCIA</h5>
          <div className="tb-exams-grid">
            {catalogs.referenceServices.map((s) => {
              const current = triageReferralState[s.id] || emptyReferralEntry();
              return (
                <div key={s.id} className="tb-exam-card">
                  <label className="tb-checkbox-line">
                    <input type="checkbox" checked={!!current.checked} onChange={() => handleReferralToggle(s.id)} />
                    {s.name}
                  </label>
                  {current.checked && (
                    <div className="tb-exam-fields">
                      <input
                        type="text"
                        placeholder="A quem foi solicitado"
                        value={current.solicitadoPara}
                        onChange={(e) => handleReferralField(s.id, 'solicitadoPara', e.target.value)}
                      />
                      <input
                        type="date"
                        value={current.dataSolicitacao}
                        onChange={(e) => handleReferralField(s.id, 'dataSolicitacao', e.target.value)}
                      />
                      {existingFiles.referralFileIds[s.id] && !current.file && (
                        <BinaryFileLink resource={BINARY_RESOURCES.tuberculosisReferralRequest} id={existingFiles.referralFileIds[s.id]} field="file" className="tb-file-link">Ver documento</BinaryFileLink>
                      )}
                      <input
                        type="file"
                        accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleReferralField(s.id, 'file', e.target.files?.[0] || null)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="tb-text-block">
        <label>Detalhamento da articulação do(s) Exame(s)</label>
        <textarea rows={4} value={examArticulacao} onChange={(e) => setExamArticulacao(e.target.value)} />
      </div>

      <div className="tb-sinan-box">
        <div>
          <strong>NOTIFICACAO SINAN REQUERIDA</strong>
          <p>Realize o download e anexe a ficha SINAN preenchida.</p>
        </div>
        <a
          href="https://portalsinan.saude.gov.br/images/documentos/Agravos/Tuberculose/Tuberculose_v5.pdf"
          target="_blank"
          rel="noreferrer"
          className="tb-btn-outline"
        >BAIXAR FICHA SINAN</a>
        {existingFiles.sinanFileId && !sinanFile && (
          <BinaryFileLink resource={BINARY_RESOURCES.tuberculosisExamArticulation} id={existingFiles.sinanFileId} field="sinan_file" className="tb-file-link">Ver ficha SINAN atual</BinaryFileLink>
        )}
        <input type="file" accept="application/pdf,.pdf" onChange={(e) => setSinanFile(e.target.files?.[0] || null)} />
      </div>
    </div>
  );

  const renderVigilanciaTab = () => (
    <div className="tb-step-card">
      <h4>3. ARTICULACAO COM A VIGILANCIA (SMS)</h4>
      <div className="tb-two-cols">
        <div>
          <label>Data de comunicacao</label>
          <input
            type="date"
            value={vigilancia.dataComunicacao}
            onChange={(e) => setVigilancia((prev) => ({ ...prev, dataComunicacao: e.target.value }))}
          />
        </div>
        <div>
          <label>Meio de comunicacao</label>
          <select
            value={vigilancia.meioComunicacao}
            onChange={(e) => setVigilancia((prev) => ({ ...prev, meioComunicacao: Number(e.target.value) || '' }))}
          >
            <option value="">Selecione...</option>
            {catalogs.communicationMethods.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>
      </div>
      <label>Status do fornecimento de medicamentos</label>
      <div className="tb-status-row">
        {catalogs.medicationSupplyStatuses.map((s) => (
          <button
            key={s.id}
            type="button"
            className={vigilancia.statusMedicamentos === s.id ? 'active' : ''}
            onClick={() => setVigilancia((prev) => ({ ...prev, statusMedicamentos: s.id }))}
          >{s.name}</button>
        ))}
      </div>
    </div>
  );

  const renderTratamentoTab = () => (
    <div className="tb-step-card">
      <h4>4. CONTROLE DE TRATAMENTO - ACOMPANHAMENTO</h4>
      <div className="tb-sub-card">
        <div className="tb-three-cols">
          <div>
            <label>Data</label>
            <input type="date" value={evolutionForm.data} onChange={(e) => setEvolutionForm((prev) => ({ ...prev, data: e.target.value }))} />
          </div>
          <div>
            <label>Peso corporal (kg)</label>
            <input
              type="number"
              min="0"
              step="0.1"
              value={evolutionForm.peso}
              onChange={(e) => setEvolutionForm((prev) => ({ ...prev, peso: normalizeNonNegativeDecimal(e.target.value) }))}
            />
          </div>
          <div>
            <label>Esquema terapeutico</label>
            <select
              value={evolutionForm.esquema}
              onChange={(e) => setEvolutionForm((prev) => ({ ...prev, esquema: Number(e.target.value) || '' }))}
            >
              <option value="">Selecione...</option>
              {catalogs.therapeuticSchemes.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="tb-two-cols">
          <div>
            <label>Adesao / TDO</label>
            <select
              value={evolutionForm.tdo}
              onChange={(e) => setEvolutionForm((prev) => ({ ...prev, tdo: e.target.value }))}
            >
              <option value="">TDO Realizado? Selecione...</option>
              {catalogs.tdoStatuses.map((opt) => (
                <option key={opt.code} value={opt.code}>{opt.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Intercorrencias</label>
            <input
              type="text"
              value={evolutionForm.intercorrencias}
              onChange={(e) => setEvolutionForm((prev) => ({ ...prev, intercorrencias: e.target.value }))}
            />
          </div>
        </div>
        <div>
          <label>Observacoes gerais</label>
          <textarea
            rows={3}
            value={evolutionForm.observacoes}
            onChange={(e) => setEvolutionForm((prev) => ({ ...prev, observacoes: e.target.value }))}
          />
        </div>
        <button type="button" className="tb-btn-small" onClick={addEvolution}>ADICIONAR EVOLUCAO</button>
      </div>

      {evolutions.length > 0 && (
        <div className="tb-timeline">
          {evolutions.map((item, index) => {
            const scheme = catalogs.therapeuticSchemes.find((s) => s.id === item.esquema);
            return (
              <div key={`${item.data}-${index}`} className="tb-timeline-item">
                <strong>{formatDateBr(item.data)}</strong>
                <p>Esquema: {scheme?.name || '-'}</p>
                <p>{item.observacoes || '-'}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderEncaminhamentosTab = () => (
    <div className="tb-step-card">
      <h4>5. ENCAMINHAMENTOS (MEDIA/ALTA COMPLEXIDADE)</h4>
      <div className="tb-sub-card">
        <div className="tb-two-cols">
          <div>
            <label>Data do encaminhamento</label>
            <input
              type="date"
              value={forwardingForm.data}
              onChange={(e) => setForwardingForm((prev) => ({ ...prev, data: e.target.value }))}
            />
          </div>
          <div>
            <label>Servico de referencia</label>
            <select
              value={forwardingForm.servico}
              onChange={(e) => setForwardingForm((prev) => ({ ...prev, servico: Number(e.target.value) || '' }))}
            >
              <option value="">Selecione...</option>
              {catalogs.referenceServices.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label>Necessidade de encaminhamento</label>
          <select
            value={forwardingForm.necessidade}
            onChange={(e) => setForwardingForm((prev) => ({ ...prev, necessidade: Number(e.target.value) || '' }))}
          >
            <option value="">Selecione...</option>
            {catalogs.referralNeeds.map((n) => (
              <option key={n.id} value={n.id}>{n.name}</option>
            ))}
          </select>
        </div>
        <button type="button" className="tb-btn-small" onClick={addForwarding}>REGISTRAR ENCAMINHAMENTO</button>
      </div>

      {forwardings.length > 0 && (
        <div className="tb-timeline">
          {forwardings.map((item, index) => {
            const service = catalogs.referenceServices.find((s) => s.id === item.servico);
            const need = catalogs.referralNeeds.find((n) => n.id === item.necessidade);
            return (
              <div key={`${item.data}-${index}`} className="tb-timeline-item">
                <strong>{formatDateBr(item.data)}</strong>
                <p>Servico: {service?.name || '-'}</p>
                <p>{need?.name || '-'}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderTransferenciaBlock = () => (
    <div className="tb-transfer-box">
      <h5>BOLETIM DE TRANSFERENCIA DE CASO - TUBERCULOSE</h5>
      <div className="tb-two-cols">
        <div>
          <label>N. SINAN (se houver)</label>
          <input
            type="text"
            value={transferencia.sinanNumero}
            onChange={(e) => setTransferencia((prev) => ({ ...prev, sinanNumero: e.target.value }))}
          />
        </div>
        <div>
          <label>Data da notificacao</label>
          <input
            type="date"
            value={transferencia.dataNotificacao}
            onChange={(e) => setTransferencia((prev) => ({ ...prev, dataNotificacao: e.target.value }))}
          />
        </div>
      </div>

      <div>
        <label>Forma clinica</label>
        <div className="tb-status-row">
          {catalogs.clinicalForms.map((f) => (
            <button
              key={f.id}
              type="button"
              className={transferencia.formaClinica === f.id ? 'active' : ''}
              onClick={() => setTransferencia((prev) => ({ ...prev, formaClinica: f.id }))}
            >{f.name}</button>
          ))}
        </div>
      </div>

      {!!transferencia.formaClinica && (
        <div>
          <label>Especificar localizacao</label>
          <input
            type="text"
            value={transferencia.extrapulmonaryLocation}
            onChange={(e) => setTransferencia((prev) => ({ ...prev, extrapulmonaryLocation: e.target.value }))}
          />
        </div>
      )}

      <div className="tb-two-cols">
        <div>
          <label>Teste tuberculínico (PPD)</label>
          {catalogs.pddResults.map((opt) => (
            <label key={opt.id} className="tb-checkbox-line">
              <input
                type="radio"
                name="ppd_result"
                checked={transferencia.ppd === opt.id}
                onChange={() => setTransferencia((prev) => ({ ...prev, ppd: opt.id }))}
              />
              {opt.name}
            </label>
          ))}
        </div>
        <div>
          <label>Drogas utilizadas</label>
          {catalogs.drugs.map((d) => (
            <label key={d.id} className="tb-checkbox-line">
              <input
                type="checkbox"
                checked={transferencia.drogas.includes(d.id)}
                onChange={() => toggleDroga(d.id)}
              />
              {d.name}
            </label>
          ))}
          {isOtherDrugSelected && (
            <input
              type="text"
              placeholder="Descreva a outra droga"
              value={transferencia.otherDrugsDescription}
              onChange={(e) => setTransferencia((prev) => ({ ...prev, otherDrugsDescription: e.target.value }))}
            />
          )}
        </div>
      </div>

      <div className="tb-two-cols">
        <div>
          <label>PPL com medicacao por (?) dias</label>
          <input
            type="number"
            min="0"
            value={transferencia.pplDias}
            onChange={(e) => setTransferencia((prev) => ({ ...prev, pplDias: normalizeNonNegativeInteger(e.target.value) }))}
          />
        </div>
        <div>
          <label>TDO realizado?</label>
          <select
            value={transferencia.tdoRealizado === null ? '' : String(transferencia.tdoRealizado)}
            onChange={(e) => setTransferencia((prev) => ({
              ...prev,
              tdoRealizado: e.target.value === '' ? null : e.target.value === 'true',
            }))}
          >
            <option value="">Selecione</option>
            <option value="true">SIM</option>
            <option value="false">NAO</option>
          </select>
        </div>
      </div>

      <div className="tb-samples-header">
        <label>Amostras de baciloscopia do escarro</label>
        <button type="button" className="tb-btn-small" onClick={addBaciloscopiaSample}>+ MAIS AMOSTRA</button>
      </div>
      <div className="tb-samples-list">
        {baciloscopiaSamples.map((sample, index) => (
          <div key={sample.id} className="tb-sample-row">
            <span>{index + 1}ª Amostra:</span>
            <label className="tb-checkbox-line">
              <input
                type="radio"
                name={`baciloscopia-${sample.id}`}
                checked={sample.resultado === true}
                onChange={() => setBaciloscopiaResult(sample.id, true)}
              />
              POSITIVO
            </label>
            <label className="tb-checkbox-line">
              <input
                type="radio"
                name={`baciloscopia-${sample.id}`}
                checked={sample.resultado === false}
                onChange={() => setBaciloscopiaResult(sample.id, false)}
              />
              NEGATIVO
            </label>
            {baciloscopiaSamples.length > 1 && (
              <button
                type="button"
                className="tb-sample-remove"
                onClick={() => removeBaciloscopiaSample(sample.id)}
                aria-label={`Remover ${index + 1}a amostra`}
              >X</button>
            )}
          </div>
        ))}
      </div>

      <div className="tb-two-cols">
        <div>
          <label>HIV status</label>
          <select
            value={transferencia.hivStatus}
            onChange={(e) => setTransferencia((prev) => ({ ...prev, hivStatus: Number(e.target.value) || '' }))}
          >
            <option value="">Selecione</option>
            {catalogs.hivStatuses.map((h) => (
              <option key={h.id} value={h.id}>{h.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Inicio do tratamento</label>
          <input
            type="date"
            value={transferencia.inicioTratamento}
            onChange={(e) => setTransferencia((prev) => ({ ...prev, inicioTratamento: e.target.value }))}
          />
        </div>
      </div>
    </div>
  );

  const renderEncerramentoTab = () => (
    <div className="tb-step-card">
      <h4>6. ALTA, TRANSFERENCIA OU ENCERRAMENTO</h4>
      <div className="tb-two-cols">
        <div>
          <label>Tipo de encerramento</label>
          <select
            value={encerramento.tipo}
            onChange={(e) => setEncerramento((prev) => ({ ...prev, tipo: Number(e.target.value) || '' }))}
          >
            <option value="">Selecione...</option>
            {catalogs.closureTypes.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Data de encerramento</label>
          <input
            type="date"
            value={encerramento.data}
            onChange={(e) => setEncerramento((prev) => ({ ...prev, data: e.target.value }))}
          />
        </div>
      </div>
      <div>
        <label>Boletim de alta ou transferencia (PDF)</label>
        {existingFiles.closureFileId && !encerramento.file && (
          <BinaryFileLink resource={BINARY_RESOURCES.tuberculosisClosureRecord} id={existingFiles.closureFileId} field="closure_file" className="tb-file-link">Ver documento atual</BinaryFileLink>
        )}
        <input
          type="file"
          accept="application/pdf,.pdf"
          onChange={(e) => setEncerramento((prev) => ({ ...prev, file: e.target.files?.[0] || null }))}
        />
      </div>

      {isTransferenciaType && renderTransferenciaBlock()}

      <div>
        <label>Observacoes gerais</label>
        <textarea
          rows={4}
          value={encerramento.observacoes}
          onChange={(e) => setEncerramento((prev) => ({ ...prev, observacoes: e.target.value }))}
        />
      </div>
    </div>
  );

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="container social-container admission-page">
        <Titulo children={<SearchInternal onSelect={handleInternalSelect} />} />
        <PageHeader
          title="Gestão de Tuberculose - PPL"
          subtitle="Protocolo de vigilância, acompanhamento e encerramento"
          internalDetails={internalDetails}
        />

        <div className="health-card tb-tabs-layout">
          <div className="tb-tabs-header">
            {TABS.map((label, index) => (
              <button
                key={label}
                type="button"
                className={`tb-tab-btn ${activeTab === index ? 'active' : ''} ${index > maxUnlockedTab ? 'disabled' : ''}`}
                onClick={() => goToTab(index)}
                disabled={index > maxUnlockedTab}
              >
                {index + 1}. {label}
              </button>
            ))}
          </div>

          {activeTab === 0 && renderTriagemTab()}
          {activeTab === 1 && renderExamesTab()}
          {activeTab === 2 && renderVigilanciaTab()}
          {activeTab === 3 && renderTratamentoTab()}
          {activeTab === 4 && renderEncaminhamentosTab()}
          {activeTab === 5 && renderEncerramentoTab()}

          <div className="tb-footer-action">
            <button
              type="button"
              className={`social-submit-button ${activeTab === TABS.length - 1 ? 'tb-finish-btn' : ''}`}
              onClick={validateAndContinue}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Salvando...'
                : activeTab === TABS.length - 1 ? 'Finalizar e salvar prontuario'
                : savedTabs.has(activeTab) && !dirtyTabs.has(activeTab) ? 'Proxima etapa'
                : savedTabs.has(activeTab) ? 'Atualizar e continuar'
                : 'Salvar e continuar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tuberculose;
